import { Component, NgZone, OnInit, ViewChild, ChangeDetectorRef, Inject, ElementRef } from '@angular/core';
import { MatTabGroup } from '@angular/material/tabs';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import firebase from 'firebase/app';
import moment from 'moment';
import { UserPlans } from 'src/app/dictionaries/UserPlans';
import { isPotentialDuplicate } from 'src/app/utils/patient-matching.utils';
import { UserRoles } from 'src/app/dictionaries/UserRoles';
import { Labels } from 'src/app/dictionaries/labels';

import { AuthService } from '../../services/auth.service';
import { UIMessagingService } from '../../services/uimessaging.service';
import { PhiAccessAuditService } from '../../services/phi-access-audit.service';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatSelect } from '@angular/material/select';

const dbUsers = firebase.firestore().collection('users');
@Component({
    selector: 'app-createpatient',
    templateUrl: './createpatient.component.html',
    styleUrls: ['./createpatient.component.scss'],
    standalone: false
})
export class CreatepatientComponent implements OnInit {
  @ViewChild('picker') datepicker: MatDatepicker<any>;
  @ViewChild('genderSelect') genderSelect: MatSelect;
  @ViewChild('firstNameInput') firstNameInput: ElementRef;
  @ViewChild('lastNameInput') lastNameInput: ElementRef;
  @ViewChild('emailAddressInput') emailAddressInput: ElementRef;
  @ViewChild('dateOfBirthInput') dateOfBirthInput: ElementRef;
  @ViewChild('patientIDInput') patientIDInput: ElementRef;
  @ViewChild('formTabGroup') formTabGroup: MatTabGroup;
  selectedTabIndex = 0;
  Labels = Labels;
  gender: string = '';
  fname: string;
  lname: string;
  patientId: string;
  legalCaseId: string;
  patientID: string;
  patientIDs: Array<{ number: string; facility: string }> = [];
  defaultPatientID: { number: string; facility: string } = { number: '', facility: '' };
  description: string;
  emailAddress: string;
  cellPhoneNumber: string;
  spouseName: string;
  errorMessage: string;
  date: UntypedFormControl = new UntypedFormControl();
  ownerID: any;
  UserRoles: { owner: string; admin: string; associate: string; consultant: string; superuser: string };
  maxDate = new Date();
  type: string;
  // Profile image properties
  profileImage: File | null = null;
  profileImageUrl: string | null = null;
  profileImagePreview: string | null = null;
  isUploadingImage: boolean = false;
  submitApproval: boolean = false;

  // Spoke duplicate-check state
  state: 'form' | 'duplicate-found' | 'not-same-acknowledged' | 'mrn-collision' | 'mrn-collision-confirm' | 'hub-duplicate-found' = 'form';
  duplicatePatient: { caseName: string; firstName: string; lastName: string; dob: string } | null = null;
  notSameAcknowledged = false;
  isSpoke = false;
  hasHubRelationship = false;

  // Hub merge-alert state
  hubDuplicatePatient: { caseName: string; firstName: string; lastName: string; dob: string } | null = null;
  showHubMergeIdForm = false;
  hubMergePatientId: string = '';
  hubMergeSelectedFacility: string = '';
  hubMergeAddingId = false;
  hubMergeError: string = '';

  // MRN collision state
  mrnCollisionCaseName: string | null = null;
  mrnCollisionPatient: { firstName: string; lastName: string; dob: string } | null = null;
  mrnCollisionSpokePatientId: string = '';
  mrnCollisionLoading = false;
  mrnCollisionLinking = false;
  private pendingPatientArgs: {
    safeFname: string; safeLname: string; safePatientIDNumber: string;
    dateString: string; ownerID: string; type: string; sharedWith: any; metadata: any;
  } | null = null;

  // Mock facility data
  facilities: Array<{ id: string; name: string }> = [
    { id: 'general_hospital', name: 'General Hospital' },
    { id: 'city_medical_center', name: 'City Medical Center' },
    { id: 'university_hospital', name: 'University Hospital' },
    { id: 'community_clinic', name: 'Community Clinic' },
    { id: 'specialty_center', name: 'Specialty Medical Center' },
    { id: 'regional_hospital', name: 'Regional Hospital' },
    { id: 'private_clinic', name: 'Private Medical Clinic' },
    { id: 'urgent_care', name: 'Urgent Care Center' }
  ];

  // Current user's facility (mandatory for first medical record)
  userFacility: string = '';

  constructor(
    public auth_$: AuthService,
    public router: Router,
    public ng: NgZone,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<CreatepatientComponent>,
    private uiMessaging_$: UIMessagingService,
    private phiAudit: PhiAccessAuditService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.UserRoles = {
      owner: UserRoles.owner,
      admin: UserRoles.admin,
      associate: UserRoles.associate,
      consultant: UserRoles.consultant,
      superuser: UserRoles.superuser
    };
    this.fname = '';
    this.lname = '';
    this.patientId = '';
    this.legalCaseId = '';
    this.patientID = '';
    this.errorMessage = '';
    this.cellPhoneNumber = '';
    this.emailAddress = '';
    this.spouseName = '';
    this.description = '';
    this.type = 'external';
    this.patientIDs = [];
  }

  onStrengthChanged(event) {
    // No password fields, so assume approval unless other fields are invalid
  }

  onPasswordChange() {
    // No password fields, so assume approval unless other fields are invalid
  }

  onPasswordBlur() {
    // No password fields, so assume approval unless other fields are invalid
  }

  onPasswordConfirmBlur() {
    // No password fields, so assume approval unless other fields are invalid
  }

  // Patient IDs management
  addPatientIDNumber() {
    // Allow adding if the first patient ID is filled
    if (this.isFirstPatientIDFilled()) {
      // Add a new patient ID with empty facility (user will select from available facilities)
      this.patientIDs.push({ number: '', facility: '' });
      this.errorMessage = ''; // Clear any existing error messages
    } else {
      this.errorMessage = 'Please fill in the first patient ID before adding additional ones';
    }
  }

  // Add default Patient ID to the array
  addDefaultPatientID() {
    if (this.defaultPatientID.number.trim() && this.defaultPatientID.facility) {
      // Check if this facility is already used
      const isFacilityAlreadyUsed = this.patientIDs.some(mr => mr.facility === this.defaultPatientID.facility);
      if (isFacilityAlreadyUsed) {
        this.errorMessage = 'This facility is already selected for another medical record';
        return;
      }

      // Add to patientIDs array
      this.patientIDs.push({
        number: this.defaultPatientID.number,
        facility: this.defaultPatientID.facility
      });

      // Clear the default values
      this.defaultPatientID = { number: '', facility: '' };
      this.errorMessage = '';
    } else {
      this.errorMessage = 'Please enter both Patient ID and select a facility';
    }
  }

  // Handle default facility selection change
  onDefaultFacilityChange(selectedFacilityId: string): void {
    this.defaultPatientID.facility = selectedFacilityId;
    this.errorMessage = ''; // Clear any existing error messages
  }

  removePatientIDNumber(index: number) {
    // Prevent removing the first patient ID (it's mandatory)
    if (index === 0) {
      this.errorMessage = 'Cannot remove the first patient ID - it is mandatory';
      return;
    }

    if (this.patientIDs.length > 1) {
      this.patientIDs.splice(index, 1);
      this.errorMessage = ''; // Clear error message on successful removal
    }
  }

  getFacilityName(facilityId: string): string {
    const facility = this.facilities.find(f => f.id === facilityId);
    return facility ? facility.name : '';
  }

  get facilitiesDisplayText(): string {
    return this.facilities.map(f => f.name).join(', ');
  }

  // Get available facilities for a specific medical record
  getAvailableFacilities(currentIndex: number): Array<{ id: string; name: string }> {
    // For the first patient ID (index 0), only show user's facility
    if (currentIndex === 0) {
      if (this.userFacility) {
        const userFacilityObj = this.facilities.find(f => f.id === this.userFacility);
        return userFacilityObj ? [userFacilityObj] : [];
      }
      return [];
    }

    // For subsequent patient IDs, show all facilities except already-used ones
    const selectedFacilityIds = this.patientIDs
      .map((mr, index) => (index !== currentIndex ? mr.facility : null))
      .filter(facility => facility && facility.trim() !== '');

    return this.facilities.filter(f => !selectedFacilityIds.includes(f.id));
  }

  // Get available facilities for the default patient ID entry, excluding already-used facilities
  getAvailableFacilitiesForDefault(): Array<{ id: string; name: string }> {
    // Get all facility IDs that are already selected in the patientIDs array
    const selectedFacilityIds = this.patientIDs
      .map(mr => mr.facility)
      .filter(facility => facility && facility.trim() !== '');

    // Return facilities that haven't been selected yet
    return this.facilities.filter(f => !selectedFacilityIds.includes(f.id));
  }

  // Check if a facility is already selected in another medical record
  isFacilityAlreadySelected(facilityId: string, currentIndex: number): boolean {
    return this.patientIDs.some((mr, index) => index !== currentIndex && mr.facility === facilityId);
  }

  // Check if user has filled the first patient ID
  hasUserFacilityPatientID(): boolean {
    // Check if the first patient ID is filled
    if (this.patientIDs.length > 0) {
      const firstPatientID = this.patientIDs[0];
      return (
        firstPatientID.number && firstPatientID.number.trim() !== '' && firstPatientID.facility === this.userFacility
      );
    }
    return false;
  }

  // Check if the first patient ID is filled (either in array or default)
  isFirstPatientIDFilled(): boolean {
    // Check if there's a patient ID in the array with a number
    const hasFilledPatientID = this.patientIDs.some(mr => mr.number && mr.number.trim() !== '');

    // Check if the default patient ID has a number
    const hasFilledDefaultPatientID = this.defaultPatientID.number && this.defaultPatientID.number.trim() !== '';

    return hasFilledPatientID || hasFilledDefaultPatientID;
  }

  // Handle facility selection change
  onFacilityChange(selectedFacilityId: string, currentIndex: number): void {
    // Clear any existing error messages
    this.errorMessage = '';

    // For the first patient ID, ensure it's the user's facility
    if (currentIndex === 0 && selectedFacilityId !== this.userFacility) {
      this.errorMessage = 'The first patient ID must be for your facility';
      this.patientIDs[currentIndex].facility = this.userFacility;
      return;
    }

    // Validate that this facility isn't already selected
    if (this.isFacilityAlreadySelected(selectedFacilityId, currentIndex)) {
      this.errorMessage = 'This facility is already selected for another patient ID';
      // Clear the selection
      this.patientIDs[currentIndex].facility = '';
      return;
    }
  }

  async ngOnInit() {
    this.authInit();
    this.auth_$.errorAuth.subscribe(error => {
      this.errorMessage = error;
    });

    // Use facilities data passed from dialog if available, otherwise use mock data
    if (this.data && this.data.facilities && Array.isArray(this.data.facilities)) {
      this.facilities = this.data.facilities.map(facility => ({
        id: facility.id,
        name: facility.name
      }));
    } else {
    }

    const currentUserUID = this.auth_$.currentUser.value.uid;
    const userobj = await dbUsers
      .where('uid', '==', currentUserUID)
      .limit(1)
      .get()
      .then(item => ({
        role: item.docs[0].data().role,
        createdBy: item.docs[0].data().createdBy,
        ownerID: item.docs[0].data().ownerID,
        facility: item.docs[0].data().facility,
        plancode: item.docs[0].data().plancode
      }));

    // Get user's facility for mandatory first medical record
    this.userFacility = userobj.facility || '';

    // Ensure the user's actual facility appears in the facilities list.
    // The list may be seeded with mock data; real facility IDs from Firestore won't match.
    if (this.userFacility && !this.facilities.some(f => f.id === this.userFacility)) {
      try {
        const facilityDoc = await firebase.firestore().collection('facilities').doc(this.userFacility).get();
        const facilityName = facilityDoc.exists ? (facilityDoc.data()!['name'] || this.userFacility) : this.userFacility;
        this.facilities = [{ id: this.userFacility, name: facilityName }, ...this.facilities];
      } catch {
        this.facilities = [{ id: this.userFacility, name: this.userFacility }, ...this.facilities];
      }
    }

    // Detect Spoke and whether a hub trust relationship exists
    this.isSpoke = userobj.plancode === UserPlans.spoke;
    if (this.isSpoke && this.userFacility) {
      const hubCheck = await firebase.firestore()
        .collection('patients')
        .where(`sharedWithFacilityMap.${this.userFacility.toLowerCase()}`, '==', true)
        .limit(1)
        .get();
      this.hasHubRelationship = !hubCheck.empty;
    }

    if (userobj.role === UserRoles.owner) {
      this.ownerID = currentUserUID;
    } else if (userobj.ownerID) {
      this.ownerID = userobj.ownerID;
    } else {
      const message = `The current user has an invalid ownerID, please contact your administrator before retry`;
      const label = 'ALERT';
      this.uiMessaging_$.toastMessage(message, label);
    }

    // Initialize medical record with user's facility if available
    if (this.userFacility) {
      const prefillId = this.data?.prefill?.patientId || '';
      this.patientIDs = [{ number: prefillId, facility: this.userFacility }];
    }

    // Pre-fill form fields from DICOM data if provided
    if (this.data?.prefill) {
      const p = this.data.prefill;

      if (p.firstname) this.fname = p.firstname;
      if (p.lastname)  this.lname = p.lastname;

      // Map DICOM sex codes (M/F/O) to the select option values (male/female)
      if (p.gender) {
        const g = p.gender.trim().toUpperCase();
        if (g === 'M') this.gender = 'male';
        else if (g === 'F') this.gender = 'female';
      }

      // Parse YYYY-MM-DD as local date to avoid UTC midnight timezone shift
      if (p.dob) {
        const parts = p.dob.split('-');
        if (parts.length === 3) {
          const localDate = new Date(+parts[0], +parts[1] - 1, +parts[2]);
          if (!isNaN(localDate.getTime())) this.date.setValue(localDate);
        }
      }

      // Also pre-fill the fallback defaultPatientID for when userFacility is not set
      if (p.patientId && !this.userFacility) {
        this.defaultPatientID = { number: p.patientId, facility: '' };
      }
    }
  }

  openDatepickerIfEmpty() {
    // Open the datepicker if the date is empty
    if (!this.date.value) {
      this.datepicker.select(new Date());
      this.datepicker.open();
    }
  }

  getErrorMessage(formControl, controlName?: string, num?): string | void {
    if (formControl.invalid) {
      if (formControl.errors?.required || (formControl.required && !formControl.value)) {
        return controlName === 'usertype' ? 'You need to select a user type.' : `You need to provide ${controlName}.`;
      }
      if (
        formControl.errors?.minlength ||
        (formControl.minLength && formControl.value && formControl.value.length < formControl.minLength)
      ) {
        return `The field ${controlName} requires at least ${formControl.minLength || 6} characters.`;
      }
      if (
        formControl.errors?.email ||
        (controlName === 'email' && formControl.value && !this.isValidEmail(formControl.value))
      ) {
        return 'A valid email address is required.';
      }
      if (formControl.errors?.whitespace) {
        return 'Please avoid blank spaces.';
      }
      if (formControl.errors?.mustMatch) {
        return "Password and confirm password don't match.";
      }
      if (formControl.errors?.passwordStrength) {
        return 'Password is not strong enough.';
      }
    }
  }

  // Public method for email validation used in template
  public isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  isFormValid(): boolean {
    // Check if we have valid Patient IDs in the array OR a valid default Patient ID
    const hasValidPatientIDs = this.patientIDs.some(mr => mr.number.trim() !== '' && mr.facility !== '');
    const hasValidDefaultPatientID =
      this.defaultPatientID.number.trim() !== '' && this.defaultPatientID.facility !== '';
    const hasAnyValidPatientID = hasValidPatientIDs || hasValidDefaultPatientID;

    const hasNoDuplicateFacilities = this.hasNoDuplicateFacilities();
    const hasValidGender = this.gender && this.gender.trim() !== '';
    const hasValidDateOfBirth = this.date.value !== null;
    const hasValidUserFacilityPatientID = this.isUserFacilityPatientIDValid();

    // Email is optional - only validate if provided
    const hasValidEmail = !this.emailAddress || this.isValidEmail(this.emailAddress);

    return (
      this.fname.trim() !== '' &&
      this.lname.trim() !== '' &&
      hasAnyValidPatientID &&
      hasValidUserFacilityPatientID &&
      hasNoDuplicateFacilities &&
      hasValidGender &&
      hasValidDateOfBirth &&
      hasValidEmail
    );
  }

  // Check if there are any duplicate facilities selected
  hasNoDuplicateFacilities(): boolean {
    const selectedFacilities = this.patientIDs
      .map(mr => mr.facility)
      .filter(facility => facility && facility.trim() !== '');

    // Include default Patient ID facility if it's set
    if (this.defaultPatientID.facility && this.defaultPatientID.facility.trim() !== '') {
      selectedFacilities.push(this.defaultPatientID.facility);
    }

    const uniqueFacilities = [...new Set(selectedFacilities)];
    return selectedFacilities.length === uniqueFacilities.length;
  }

  // Check if the medical record for user's facility is valid
  isUserFacilityPatientIDValid(): boolean {
    // If user has no facility, allow any facility
    if (!this.userFacility) {
      return true;
    }

    // Check if there's a valid record for user's facility in the array
    const userFacilityRecord = this.patientIDs.find(mr => mr.facility === this.userFacility);
    if (userFacilityRecord) {
      const hasNumber = userFacilityRecord.number && userFacilityRecord.number.trim() !== '';
      const hasCorrectFacility = userFacilityRecord.facility === this.userFacility;
      return hasNumber && hasCorrectFacility;
    }

    // Check if default Patient ID is for user's facility
    if (this.defaultPatientID.facility === this.userFacility) {
      const hasNumber = this.defaultPatientID.number && this.defaultPatientID.number.trim() !== '';
      return hasNumber;
    }

    // If no Patient IDs exist yet, it's valid (user can still add them)
    if (this.patientIDs.length === 0 && !this.defaultPatientID.facility) {
      return true;
    }

    return false;
  }

  authInit() {
    this.auth_$.user.subscribe(user => {
      if (user !== null && user !== undefined) {
        const db = firebase.firestore();

        db.collection('users')
          .where('uid', '==', user.uid)
          .get()
          .then(querySnapshot => {
            // Check if there's at least one document and only process it once
            if (querySnapshot.docs.length > 0) {
              const doc = querySnapshot.docs[0]; // Take the first document
              if (doc.data()['role'] !== UserRoles.client && doc.data()['role'] !== UserRoles.admin) {
                this.determineRoute(doc.data()['role'], user);
              }
            }
          });
      }
    });
  }

  cleanBlankSpaces(str: string) {
    return str.replace(/[^(A-Za-z0-9)]/g, '-');
  }

  private sanitizeTextField(value: string, maxLength: number): string {
    if (!value) return '';
    return value.replace(/<[^>]*>/g, '').trim().substring(0, maxLength);
  }

  createPatient() {
    if (!this.validateFields()) {
      this.handleCreatePatient();
    } else {
      this.auth_$.firePatientCantAdd();
    }
  }

  private async handleCreatePatient() {
    const dateString = this.date.value ? this.getDateString(moment(this.date.value).add(1, 'M').toDate()) : null;

    // Upload profile image if selected
    let profileImageUrl = null;
    if (this.profileImage) {
      profileImageUrl = await this.uploadProfileImage();
      if (!profileImageUrl) {
        // Upload failed, error message already set in uploadProfileImage
        return;
      }
    }

    // Get the first valid medical record number for the primary record
    let primaryPatientID = this.patientIDs.find(mr => mr.number.trim() !== '' && mr.facility !== '');

    // If no Patient ID in array, use the default one
    if (!primaryPatientID && this.defaultPatientID.number.trim() && this.defaultPatientID.facility) {
      primaryPatientID = this.defaultPatientID;
    }

    if (!primaryPatientID) {
      this.errorMessage = 'At least one medical record number with facility is required';
      return;
    }

    // Add all medical record numbers to metadata (including default if used)
    const allPatientIDs = [...this.patientIDs];
    const conditions =
      this.defaultPatientID.number.trim() &&
      this.defaultPatientID.facility &&
      !this.patientIDs.some(
        mr => mr.number === this.defaultPatientID.number && mr.facility === this.defaultPatientID.facility
      );

    if (conditions) {
      allPatientIDs.push(this.defaultPatientID);
    }

    const metadata: any = {
      cellphonNumber: this.cellPhoneNumber,
      emailAddress: this.emailAddress,
      spouseName: this.spouseName,
      description: this.description,
      gender: this.gender,
      ...(profileImageUrl && { patientPicture: profileImageUrl }),
      medical_record_numbers: allPatientIDs
        .filter(mr => mr.number.trim() !== '' && mr.facility !== '')
        .map(mr => ({
          facility: mr.facility,
          isExisting: true,
          value: mr.number,
          number: mr.number
        }))
    };

    const sharedWith: any = {
      facilities: allPatientIDs
        .filter(mr => mr.number.trim() !== '' && mr.facility !== '')
        .map(mr => ({
          facility: mr.facility,
          hub: mr.facility
        }))
    };

    const safeFname = this.sanitizeTextField(this.fname, 100);
    const safeLname = this.sanitizeTextField(this.lname, 100);
    const safePatientIDNumber = this.sanitizeTextField(primaryPatientID.number, 50);
    const safeDescription = this.sanitizeTextField(this.description, 500);

    if (!safeFname || !safeLname || !safePatientIDNumber) {
      this.errorMessage = 'Invalid input detected in required fields. Please check your entries.';
      return;
    }

    metadata.description = safeDescription;

    const args = { safeFname, safeLname, safePatientIDNumber, dateString, ownerID: this.ownerID, type: this.type, sharedWith, metadata };

    if (!this.isSpoke) {
      const hubDup = await this.checkForHubDuplicatePatient(dateString);
      if (hubDup) {
        this.hubDuplicatePatient = hubDup;
        this.pendingPatientArgs = args;
        this.state = 'hub-duplicate-found';
        return;
      }
    }

    if (this.isSpoke && this.hasHubRelationship) {
      const duplicate = await this.checkForDuplicatePatient(dateString);
      if (duplicate) {
        this.duplicatePatient = duplicate;
        this.pendingPatientArgs = args;
        this.state = 'duplicate-found';
        return;
      }
    }

    await this.doCreatePatient(args);
  }

  private async doCreatePatient(args: { safeFname: string; safeLname: string; safePatientIDNumber: string; dateString: string; ownerID: string; type: string; sharedWith: any; metadata: any } | null) {
    if (!args) return;
    const caseName = args.safePatientIDNumber.toLowerCase().replace(/ /g, '-');
    try {
      const result = await this.auth_$.createPatientMedical(
        args.safeFname,
        args.safeLname.toLowerCase(),
        caseName,
        args.safePatientIDNumber,
        args.dateString,
        args.ownerID,
        args.type,
        args.sharedWith,
        args.metadata
      );
      if (result) {
        this.phiAudit.logPhiAccess('patient', caseName, 'create');
        this.dialogRef.close();
      }
    } catch (err: any) {
      // Firestore permission-denied means the caseName is owned by another facility.
      // Trigger the MRN collision flow so the Spoke can link their own ID instead.
      if (err?.code === 'permission-denied' || err?.message?.includes('Missing or insufficient permissions')) {
        await this.startMrnCollisionFlow(caseName);
      } else {
        console.error('Error creating patient:', err);
        this.errorMessage = 'Error creating patient';
      }
    }
  }

  private async startMrnCollisionFlow(caseName: string) {
    this.mrnCollisionCaseName = caseName;
    this.mrnCollisionPatient = null;
    this.mrnCollisionLoading = true;
    this.state = 'mrn-collision';

    try {
      const fn = firebase.functions().httpsCallable('patientLinks-getIdentityForLink');
      const result = await fn({ caseName });
      const data = result.data as { found: boolean; firstName?: string; lastName?: string; dob?: string };
      if (data.found) {
        this.mrnCollisionPatient = {
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          dob: data.dob || '',
        };
      }
    } catch (err) {
      console.error('getPatientIdentityForLink error:', err);
      this.mrnCollisionPatient = null;
    } finally {
      this.mrnCollisionLoading = false;
    }
  }

  cancelMrnCollision() {
    this.state = 'form';
    this.mrnCollisionCaseName = null;
    this.mrnCollisionPatient = null;
    this.mrnCollisionSpokePatientId = '';
    this.errorMessage = '';
  }

  async confirmMrnCollisionLink() {
    if (!this.mrnCollisionCaseName || !this.mrnCollisionSpokePatientId?.trim()) return;
    this.mrnCollisionLinking = true;
    this.errorMessage = '';
    try {
      const fn = firebase.functions().httpsCallable('patientLinks-createLink');
      await fn({
        caseName: this.mrnCollisionCaseName,
        spokePatientId: this.mrnCollisionSpokePatientId.trim(),
      });
      this.dialogRef.close();
    } catch (err: any) {
      console.error('createPatientLink error:', err);
      this.errorMessage = err?.message || 'Error linking patient. Please try again.';
    } finally {
      this.mrnCollisionLinking = false;
    }
  }

  async confirmSamePatient() {
    if (!this.duplicatePatient || !this.pendingPatientArgs) return;
    const spokePatientID = this.pendingPatientArgs.metadata.medical_record_numbers
      .find((mr: any) => mr.facility === this.userFacility);
    if (spokePatientID) {
      try {
        await firebase.firestore().collection('patients').doc(this.duplicatePatient.caseName).update({
          'metadata.medical_record_numbers': firebase.firestore.FieldValue.arrayUnion(spokePatientID),
          [`sharedWithFacilityMap.${this.userFacility.toLowerCase()}`]: true
        });
      } catch (err) {
        console.error('Error linking patient ID:', err);
        this.errorMessage = 'Error linking patient. Please try again.';
        return;
      }
    }
    this.dialogRef.close();
  }

  confirmDifferentPatient() {
    this.state = 'not-same-acknowledged';
    this.notSameAcknowledged = false;
  }

  async proceedWithNewPatientCreation() {
    if (!this.notSameAcknowledged || !this.pendingPatientArgs) return;
    await this.doCreatePatient(this.pendingPatientArgs);
  }

  viewExistingHubPatient() {
    if (!this.hubDuplicatePatient) return;
    this.dialogRef.close();
    this.router.navigate(['/patient-overview', this.hubDuplicatePatient.caseName]);
  }

  showAddIdToExistingForm() {
    this.hubMergePatientId = this.pendingPatientArgs?.safePatientIDNumber || '';
    this.hubMergeSelectedFacility = this.userFacility || '';
    this.hubMergeError = '';
    this.showHubMergeIdForm = true;
  }

  async confirmAddPatientIdToExisting() {
    if (!this.hubDuplicatePatient || !this.hubMergePatientId || !this.hubMergeSelectedFacility) return;
    this.hubMergeAddingId = true;
    this.hubMergeError = '';
    try {
      const newMrn = {
        facility: this.hubMergeSelectedFacility,
        isExisting: true,
        value: this.hubMergePatientId,
        number: this.hubMergePatientId,
      };
      await firebase.firestore().collection('patients').doc(this.hubDuplicatePatient.caseName).update({
        'metadata.medical_record_numbers': firebase.firestore.FieldValue.arrayUnion(newMrn),
      });
      this.dialogRef.close();
    } catch (err: any) {
      console.error('Error adding patient ID to existing patient:', err);
      this.hubMergeError = 'Error adding patient ID. Please try again.';
    } finally {
      this.hubMergeAddingId = false;
    }
  }

  async createAnywayHub() {
    if (!this.pendingPatientArgs) return;
    this.hubDuplicatePatient = null;
    this.state = 'form';
    await this.doCreatePatient(this.pendingPatientArgs);
  }

  private async checkForHubDuplicatePatient(dateString: string) {
    const snapshot = await firebase.firestore()
      .collection('patients')
      .where('owners', 'array-contains', this.ownerID)
      .get();

    for (const doc of snapshot.docs) {
      const data = doc.data();
      if (isPotentialDuplicate(
        { firstName: this.fname, lastName: this.lname, dob: dateString },
        { firstName: data['FirstName'], lastName: data['LastName'], dob: data['DateOfBirth'] }
      )) {
        return { caseName: doc.id, firstName: data['FirstName'], lastName: data['LastName'], dob: data['DateOfBirth'] };
      }
    }
    return null;
  }

  private async checkForDuplicatePatient(dateString: string) {
    const snapshot = await firebase.firestore()
      .collection('patients')
      .where(`sharedWithFacilityMap.${this.userFacility.toLowerCase()}`, '==', true)
      .get();

    for (const doc of snapshot.docs) {
      const data = doc.data();
      if (isPotentialDuplicate(
        { firstName: this.fname, lastName: this.lname, dob: dateString },
        { firstName: data['FirstName'], lastName: data['LastName'], dob: data['DateOfBirth'] }
      )) {
        return { caseName: doc.id, firstName: data['FirstName'], lastName: data['LastName'], dob: data['DateOfBirth'] };
      }
    }
    return null;
  }

  private validateFields() {
    const hasValidPatientIDs = this.patientIDs.some(mr => mr.number.trim() !== '' && mr.facility !== '');
    const hasValidDefaultPatientID =
      this.defaultPatientID.number.trim() !== '' && this.defaultPatientID.facility !== '';
    const hasAnyValidPatientID = hasValidPatientIDs || hasValidDefaultPatientID;
    const hasNoDuplicateFacilities = this.hasNoDuplicateFacilities();
    const hasValidGender = this.gender && this.gender.trim() !== '';
    const hasValidDateOfBirth = this.date.value !== null;
    const hasValidUserFacilityPatientID = this.isUserFacilityPatientIDValid();

    // Email is optional - only validate if provided
    const hasValidEmail = !this.emailAddress || this.isValidEmail(this.emailAddress);

    return (
      this.fname.trim() === '' ||
      this.lname.trim() === '' ||
      !hasAnyValidPatientID ||
      !hasValidUserFacilityPatientID ||
      !hasNoDuplicateFacilities ||
      !hasValidGender ||
      !hasValidDateOfBirth ||
      !hasValidEmail
    );
  }

  determineRoute(role: any, { uid, email }) {
    let url = '/';
    if (role === UserRoles.consultant) {
      url = `search/${uid}/${email}/${role}`;
    }
    this.ng.run(() => this.router.navigateByUrl(url));
  }

  goBack() {
    window.history.back();
  }

  private getDateString(date: Date) {
    return `${date.getMonth().toString()}/${date.getDate().toString()}/${date.getFullYear().toString()}`;
  }

  // Profile image handling methods
  onProfileImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      // Validate file type
      if (!this.isValidImageType(file)) {
        this.errorMessage = 'Please select a valid image file (JPG, JPEG, PNG, GIF)';
        this.clearFileInput();
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.errorMessage = 'Image file size must be less than 5MB';
        this.clearFileInput();
        return;
      }

      this.profileImage = file;
      this.errorMessage = '';

      // Create preview
      const reader = new FileReader();
      reader.onload = e => {
        this.ng.run(() => {
          this.profileImagePreview = e.target?.result as string;
        });
      };
      reader.onerror = () => {
        this.ng.run(() => {
          this.errorMessage = 'Error reading the selected image file';
          this.clearFileInput();
        });
      };
      reader.readAsDataURL(file);
    }
  }

  private isValidImageType(file: File): boolean {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    return allowedTypes.includes(file.type);
  }

  private clearFileInput(): void {
    const fileInput = document.getElementById('profileImageInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    this.profileImage = null;
    this.profileImagePreview = null;
  }

  removeProfileImage(): void {
    this.profileImage = null;
    this.profileImagePreview = null;
    this.profileImageUrl = null;
    this.clearFileInput();
  }

  private async uploadProfileImage(): Promise<string | null> {
    if (!this.profileImage) {
      return null;
    }

    try {
      this.isUploadingImage = true;

      // Create unique filename
      const timestamp = Date.now();
      const fileName = `profile_${timestamp}_${this.profileImage.name}`;
      const currentUID = firebase.auth().currentUser?.uid;
      const filePath = `users/${currentUID}/patient-profiles/${fileName}`;

      // Upload to Firebase Storage
      const storageRef = firebase.storage().ref().child(filePath);
      const uploadTask = await storageRef.put(this.profileImage);

      // Get download URL
      const downloadURL = await uploadTask.ref.getDownloadURL();
      this.profileImageUrl = downloadURL;

      return downloadURL;
    } catch (error) {
      console.error('Error uploading profile image:', error);
      this.errorMessage = 'Failed to upload profile image. Please try again.';
      return null;
    } finally {
      this.isUploadingImage = false;
    }
  }

  // Get list of missing required fields for the legend
  getMissingRequiredFields(): Array<{ field: string; label: string; elementId: string }> {
    const missingFields: Array<{ field: string; label: string; elementId: string }> = [];

    // Check first name
    if (!this.fname || this.fname.trim() === '') {
      missingFields.push({ field: 'fname', label: 'First Name', elementId: 'firstNameInput' });
    }

    // Check last name
    if (!this.lname || this.lname.trim() === '') {
      missingFields.push({ field: 'lname', label: 'Last Name', elementId: 'lastNameInput' });
    }

    // Check patient ID - use smarter targeting
    const hasValidPatientIDs = this.patientIDs.some(mr => mr.number.trim() !== '' && mr.facility !== '');
    const hasValidDefaultPatientID =
      this.defaultPatientID.number.trim() !== '' && this.defaultPatientID.facility !== '';

    if (!hasValidPatientIDs && !hasValidDefaultPatientID) {
      // Determine the correct element ID based on current form state
      let patientIDElementId = 'patientID_0';

      if (this.patientIDs.length === 0) {
        // Default patient ID field
        patientIDElementId = 'patientID_0';
      } else {
        // Find first empty patient ID in array
        for (let i = 0; i < this.patientIDs.length; i++) {
          if (!this.patientIDs[i].number || this.patientIDs[i].number.trim() === '') {
            patientIDElementId = `patientID_${i}`;
            break;
          }
        }
      }

      missingFields.push({ field: 'patientID', label: 'Patient ID', elementId: patientIDElementId });
    }

    // Check gender
    if (!this.gender || this.gender.trim() === '') {
      missingFields.push({ field: 'gender', label: 'Gender', elementId: 'genderSelect' });
    }

    // Check date of birth
    if (!this.date.value) {
      missingFields.push({ field: 'dateOfBirth', label: 'Date of Birth', elementId: 'dateOfBirthInput' });
    }

    // Check email if provided but invalid
    if (this.emailAddress && !this.isValidEmail(this.emailAddress)) {
      missingFields.push({ field: 'email', label: 'Email Address', elementId: 'emailAddressInput' });
    }

    return missingFields;
  }

  // Focus on a specific field when clicked from the legend
  focusOnField(elementId: string): void {
    // Add a small delay to ensure DOM is fully rendered
    setTimeout(() => {
      this.performFieldFocus(elementId);
    }, 100);
  }

  // Handle mouse up events on field links (workaround for click event issues)
  onMouseUp(fieldLabel: string): void {
    this.triggerFocusFromMouseUp(fieldLabel);
  }

  // Workaround method to trigger focus on mouseup
  private triggerFocusFromMouseUp(fieldLabel: string): void {
    // Map field labels to element IDs and tab indices (0=Basic Info, 1=Personal Details, 2=Contact & Photo)
    const fieldMap: Record<string, { elementId: string; tabIndex: number }> = {
      'First Name':    { elementId: 'firstNameInput',   tabIndex: 0 },
      'Last Name':     { elementId: 'lastNameInput',    tabIndex: 0 },
      'Patient ID':    { elementId: 'patientID_0',      tabIndex: 0 },
      'Gender':        { elementId: 'genderSelect',     tabIndex: 1 },
      'Date of Birth': { elementId: 'dateOfBirthInput', tabIndex: 1 },
      'Email Address': { elementId: 'emailAddressInput',tabIndex: 2 },
    };

    const mapping = fieldMap[fieldLabel];
    if (!mapping) return;

    if (this.formTabGroup && this.formTabGroup.selectedIndex !== mapping.tabIndex) {
      this.selectedTabIndex = mapping.tabIndex;
      this.formTabGroup.selectedIndex = mapping.tabIndex;
    }

    this.focusOnField(mapping.elementId);
  }

  private performFieldFocus(elementId: string): void {
    let elementToFocus: HTMLElement | null = null;
    let shouldOpenPicker = false;

    // Map element IDs to ViewChild references or DOM queries
    switch (elementId) {
      case 'firstNameInput':
        elementToFocus =
          this.firstNameInput?.nativeElement ||
          document.getElementById('firstNameInput') ||
          document.querySelector('input[id="firstNameInput"]') ||
          document.querySelector('#firstNameInput') ||
          document.querySelector('input[placeholder*="First Name"]');
        break;

      case 'lastNameInput':
        elementToFocus = this.lastNameInput?.nativeElement || document.getElementById('lastNameInput');
        break;

      case 'emailAddressInput':
        elementToFocus = this.emailAddressInput?.nativeElement || document.getElementById('emailAddressInput');
        break;

      case 'genderSelect':
        // Handle mat-select specially
        this.focusOnGenderSelect();
        return;

      case 'dateOfBirthInput':
        elementToFocus = this.dateOfBirthInput?.nativeElement || document.getElementById('dateOfBirthInput');
        shouldOpenPicker = true;
        break;

      default:
        // Handle patient ID field pattern or any other element
        if (elementId.startsWith('patientID_')) {
          elementToFocus = this.findPatientIDField();
        } else {
          elementToFocus = document.getElementById(elementId);
        }
        break;
    }

    if (elementToFocus) {
      this.scrollToElement(elementToFocus);

      // Focus after scroll animation
      setTimeout(() => {
        if (elementToFocus) {
          try {
            elementToFocus.focus();

            // Select text content for inputs
            if (elementToFocus instanceof HTMLInputElement) {
              elementToFocus.select();
            }

            // Open date picker if needed
            if (shouldOpenPicker && this.datepicker) {
              setTimeout(() => {
                this.datepicker.open();
              }, 100);
            }
          } catch (error) {
            // Silently handle focus errors
          }
        }
      }, 300);
    }
  }

  // Helper method to find the correct patient ID field
  private findPatientIDField(): HTMLElement | null {
    // Case 1: patientIDs array is empty, look for the default patient ID field
    if (this.patientIDs.length === 0) {
      if (this.patientIDInput?.nativeElement) {
        return this.patientIDInput.nativeElement;
      }
      return document.getElementById('patientID_0');
    }

    // Case 2: patientIDs array has items, look for the first empty one or just the first one
    else {
      // Find the first empty patient ID field in the array
      for (let i = 0; i < this.patientIDs.length; i++) {
        const patientID = this.patientIDs[i];
        if (!patientID.number || patientID.number.trim() === '') {
          const element = document.getElementById(`patientID_${i}`);
          if (element) {
            return element;
          }
        }
      }

      // If no empty fields, focus on the first one
      return document.getElementById('patientID_0');
    }
  }

  // Helper method to focus on gender select field
  private focusOnGenderSelect(): void {
    try {
      if (this.genderSelect) {
        const selectElement = this.genderSelect._elementRef?.nativeElement || document.getElementById('genderSelect');

        if (selectElement) {
          this.scrollToElement(selectElement);
          setTimeout(() => {
            try {
              this.genderSelect.focus();
              setTimeout(() => {
                this.genderSelect.open();
              }, 100);
            } catch (error) {
              selectElement.focus();
            }
          }, 300);
        }
      } else {
        const genderElement = document.getElementById('genderSelect');
        if (genderElement) {
          this.scrollToElement(genderElement);
          setTimeout(() => {
            genderElement.focus();
          }, 300);
        }
      }
    } catch (error) {
      // Silently handle focus errors
    }
  }

  // Helper method to scroll an element into view
  private scrollToElement(element: HTMLElement | null): void {
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
}
